import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Iter209bi — Dialog condiviso per editing di una voce KB (Knowledge Base)
 * dei chatbot amministrati (Chatbot Pubblico, Chatbot Pro, Chatbot Operator).
 * Campi editabili: titolo/descrizione, priorità, tag.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - onSaved: (updatedItem) => void   // chiamata dopo il PATCH riuscito
 *  - item: { _id, id, title, description, priority, tags, is_active, ... }
 *  - endpointBase: string             // es. "/admin/chatbot-tech/knowledge"
 *  - testidPrefix: string             // es. "chatbot-tech-kb"
 */
export default function KbEditDialog({ open, onClose, onSaved, item, endpointBase, testidPrefix }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: 0,
    tags: "",
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setForm({
      title: item.title || "",
      description: item.description || "",
      priority: Number(item.priority) || 0,
      tags: (item.tags || []).join(", "),
      is_active: item.is_active !== false,
    });
  }, [item]);

  if (!item) return null;
  const id = item._id || item.id;

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Titolo obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: Number(form.priority) || 0,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        is_active: form.is_active,
      };
      await api.patch(`${endpointBase}/${id}`, payload);
      toast.success("Modifiche salvate");
      onSaved?.({ ...item, ...payload });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid={`${testidPrefix}-edit-dialog`}>
        <DialogHeader>
          <DialogTitle>Modifica voce KB</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titolo</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Es. DPR 380/2001 - Testo unico edilizia"
              data-testid={`${testidPrefix}-edit-title`}
            />
          </div>
          <div>
            <Label className="text-xs">Descrizione (opzionale)</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-slate-200 p-2 text-sm"
              placeholder="Breve descrizione del contenuto"
              data-testid={`${testidPrefix}-edit-description`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Priorità (0-10)</Label>
              <Input
                type="number"
                min={0}
                max={10}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid={`${testidPrefix}-edit-priority`}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid={`${testidPrefix}-edit-active`}
                />
                Attivo
              </label>
            </div>
          </div>
          <div>
            <Label className="text-xs">Tag (separati da virgola)</Label>
            <Input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="edilizia, sanatoria, dpr"
              data-testid={`${testidPrefix}-edit-tags`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid={`${testidPrefix}-edit-cancel`}>
            Annulla
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="ht-gradient border-0 text-white"
            data-testid={`${testidPrefix}-edit-save`}
          >
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
